import { Avatar, ButtonV2 } from '@cimplico/components';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { DOMAIN } from '../../../../config';
import { useAuthProfileContext } from '../../../../providers/AuthProfileProvider';
import { useUserContext } from '../../../../providers/UserProvider';
import { useCurrentUser } from '../../../../queries/use-current-user';
import { useUser } from '../../../../queries/use-user';
import { useUserFirms } from '../../../../queries/use-user-firms';
import { Firm } from '../../../../types/firm';
import { User } from '../../../../types/user';
import { parseTitle } from '../../../../utilities/formatting';

type ExistingFirmSelectProps = {
    user: User;
};

function ExistingFirmSelect({ user }: ExistingFirmSelectProps): JSX.Element {
    const profile = useAuthProfileContext();
    const firmsQuery = useUserFirms(user.id);
    const firms = firmsQuery.data?.firms ?? [];
    const tokenFirms = profile[`${DOMAIN}/workpapers`].firms;

    return (
        <div>
            <p className="text-sm text-contentPrimary font-medium mb-2">Your firms</p>
            {firms.length === 0 && <p className="text-sm text-contentSecondary">You&apos;re not apart of any firms.</p>}

            <div className="flex flex-col gap-2 max-h-[500px] overflow-auto p-1">
                {firmsQuery.isLoading && (
                    <>
                        {tokenFirms.map((tf: { id: string }) => {
                            return <div key={tf.id} className="bg-gray-200 animate-pulse rounded-md min-h-[50px] w-full"></div>;
                        })}
                    </>
                )}
                {firms.length > 0 && (
                    <>
                        {firms
                            .sort((a, b) => (a.name > b.name ? 1 : -1))
                            .map((firm) => {
                                return <LaunchFirm firm={firm} key={firm.id} />;
                            })}
                    </>
                )}
            </div>
        </div>
    );
}

type LaunchFirmProps = {
    firm: Firm;
};

function LaunchFirm({ firm }: LaunchFirmProps): JSX.Element {
    return (
        <div className="p-2 border border-borderPrimary rounded-md relative">
            <div className="flex items-center gap-2">
                <Avatar size="sm" color={firm.avatarHexColour} alt={parseTitle(firm.name)} type="rounded" />
                <p className="font-medium text-contentPrimary">{firm.name}</p>
                <span className="hover:translate-x-1 transition-transform right-4 absolute">
                    <a href={`/${firm.shortId}`}>
                        <ArrowRightIcon className="h-5 w-5 text-contentPrimary" />
                    </a>
                </span>
            </div>
        </div>
    );
}

export default ExistingFirmSelect;
